import { z } from 'zod';
import {
  CARRIER_BID_VALIDITY_DAYS_MAX,
  CARRIER_BID_VALIDITY_DAYS_MIN,
  CarrierBidCurrencySchema,
  RequestCarrierState,
} from './status.js';

// WHY (#569): a request-carrier bid is one commercial quote a carrier
//   submits against a logistics request. A carrier may submit MULTIPLE
//   bids per request — typically one per unit basis (`kg` / `m3` /
//   `piece`). Winner selection operates on a specific bid
//   (`Request.winnerBidId`), not on the carrier as a whole. Total cost
//   for the bid is computed client-side as `pricePerUnit *
//   request.weight` (`'kg'`), `pricePerUnit * request.volume` (`'m3'`),
//   or `pricePerUnit * SKU count` (`'piece'`, #1051). `'piece'` lives in
//   the wire enum exactly; localization is at the i18n layer only.
export const RequestCarrierBidUnitSchema = z.enum(['kg', 'm3', 'piece']);
export type RequestCarrierBidUnit = z.infer<typeof RequestCarrierBidUnitSchema>;

// #703 — bid scope discriminator. `REQUEST` is the default primary bid
//   covering the whole request (current behavior); the others target a
//   specific sub-resource referenced by `scopeRefId`:
//     - `ITEM` -> `RequestItem.id`
//     - `BOX` -> `RequestBox.id`
//     - `BOX_ITEM` -> `RequestBoxItem.id`
//     - `BOX_GROUP` -> `<boxId>:<groupOrdinal>` composite token
//        (groupOrdinal landed by #704).
export const CarrierBidScopeSchema = z.enum(['REQUEST', 'ITEM', 'BOX', 'BOX_ITEM', 'BOX_GROUP']);
export type CarrierBidScope = z.infer<typeof CarrierBidScopeSchema>;

/**
 * @aiPurpose wire projection of one `RequestCarrierBid` row. UUID `id`
 *   matches the server-minted `RequestCarrierBid.id @default(uuid())`
 *   and is the value carried by `Request.winnerBidId` when this bid
 *   is selected as the winner.
 * @aiInvariant `pricePerUnit` is the price for one unit of the basis
 *   named in `unit` (`'kg' | 'm3' | 'piece'`). `comment` is always
 *   present (empty string when omitted) so consumers can render the
 *   slot without conditional spreading. `daysMin`/`daysMax` form a
 *   transit-time range (#1051): both null together (legacy/no value)
 *   or both positive integers with `daysMin <= daysMax`. Stale clients
 *   that omit the keys round-trip as both-null.
 * @aiSecurity bid mutations gate the same way as carrier mutations
 *   (assigned logist or admin); winner pick stays manager/admin.
 */
export const RequestCarrierBidSchema = z
  .object({
    id: z.string().uuid(),
    pricePerUnit: z.number().nonnegative(),
    currency: CarrierBidCurrencySchema,
    unit: RequestCarrierBidUnitSchema,
    daysMin: z.number().int().positive().nullable().optional(),
    daysMax: z.number().int().positive().nullable().optional(),
    comment: z.string(),
    // WHY (#585): quote-validity anchor distinct from Prisma `updatedAt`.
    //   Set explicitly by the service on create and refreshed only when
    //   commercial terms change (`pricePerUnit`, `currency`, `unit`,
    //   `daysMin`, `daysMax`, `validityDays`). Comment-only edits do
    //   NOT refresh it. Validity expires at `quotedAt + validityDays days`.
    quotedAt: z.string().datetime(),
    // WHY (#585): rolling validity window in days. Default 14, bounded
    //   1..365 at every write surface. Expired bids stay visible and
    //   selectable as winner — the UI shows an explicit warning before
    //   submit.
    validityDays: z
      .number()
      .int()
      .min(CARRIER_BID_VALIDITY_DAYS_MIN)
      .max(CARRIER_BID_VALIDITY_DAYS_MAX),
    // #703 — primary vs override discriminator + target id. Primary
    // bids carry `scope = 'REQUEST'` + `scopeRefId = null`; overrides
    // carry a non-`REQUEST` scope + a non-null scopeRefId. Service
    // validates the scopeRefId resolves to a sub-resource of the same
    // request.
    scope: CarrierBidScopeSchema,
    scopeRefId: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine((bid, ctx) => {
    if (bid.scope === 'REQUEST') {
      if (bid.scopeRefId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scopeRefId'],
          message: 'must be null when scope = REQUEST',
        });
      }
    } else if (bid.scopeRefId === null || bid.scopeRefId.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeRefId'],
        message: 'required when scope != REQUEST',
      });
    }
    refineBidDayRange(bid.daysMin, bid.daysMax, ctx);
  });
export type RequestCarrierBid = z.infer<typeof RequestCarrierBidSchema>;

// WHY (#1051): transit-day range invariant — both sides must be null
//   together (legacy / no value) or both positive integers with
//   `daysMin <= daysMax`. Exposed as a shared helper so the China and
//   Russia wire schemas + create/patch DTOs apply the same rule.
//   `daysMin`/`daysMax` are accepted as `number | null | undefined` to
//   match the optional+nullable pattern at the schema boundaries.
export function refineBidDayRange(
  daysMin: number | null | undefined,
  daysMax: number | null | undefined,
  ctx: z.RefinementCtx,
): void {
  const minPresent = daysMin !== null && daysMin !== undefined;
  const maxPresent = daysMax !== null && daysMax !== undefined;
  if (minPresent !== maxPresent) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [minPresent ? 'daysMax' : 'daysMin'],
      message: 'daysMin and daysMax must be set together or both omitted',
    });
    return;
  }
  if (minPresent && maxPresent) {
    if (!Number.isInteger(daysMin) || daysMin <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['daysMin'],
        message: 'must be a positive integer',
      });
    }
    if (!Number.isInteger(daysMax) || daysMax <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['daysMax'],
        message: 'must be a positive integer',
      });
    }
    if (daysMin > daysMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['daysMax'],
        message: 'daysMax must be >= daysMin',
      });
    }
  }
}

// WHY (#585): client/server-side helper for the carrier-bid validity
//   contract. Computes `validUntil = quotedAt + validityDays days` and
//   `isExpired(now)` against the supplied reference time. Pure function,
//   no Date subtraction surprises (DST/timezone math collapses through
//   epoch millis). Exported so the web layer and any future analytics
//   path agree on the rule.
export function computeBidValidUntil(bid: { quotedAt: string; validityDays: number }): Date {
  const quoted = new Date(bid.quotedAt);
  // Adding milliseconds keeps the function timezone-free; the wire
  // `quotedAt` is an ISO instant.
  return new Date(quoted.getTime() + bid.validityDays * 24 * 60 * 60 * 1000);
}

export function isBidExpired(
  bid: { quotedAt: string; validityDays: number },
  now: Date = new Date(),
): boolean {
  return computeBidValidUntil(bid).getTime() <= now.getTime();
}

// WHY (#1052): carrier-level insurance quote. Originally at-most-one per
//   `RequestCarrier`; #1163 lifts that to "exactly one REQUEST-scope
//   primary plus any number of overrides on `ITEM` / `BOX` / `BOX_ITEM` /
//   `BOX_GROUP`". `percent` is decimal 0..100, `comment` is always
//   present (empty string when omitted) so renderers can avoid
//   conditional spreading. `quotedAt` mirrors the bid-validity anchor
//   pattern: server stamps it on create and refreshes on update/
//   reconfirm. `needsReconfirm` is raised by the service when manager-
//   side cargo value or currency changes after the quote was logged;
//   cleared by the next add/update/reconfirm of that specific row.
//   `amount` and `currency` are populated read-side ONLY on the
//   REQUEST-scope primary (`cargoValue * percent / 100`); overrides
//   leave both null because they do not carry a stand-alone insured
//   value. The `scope` / `scopeRefId` pair mirrors `RequestCarrierBid`
//   exactly (#703): primary carries `scope = 'REQUEST'` +
//   `scopeRefId = null`; overrides carry a non-REQUEST scope and a
//   non-null `scopeRefId`. The service validates the ref resolves to a
//   sub-resource of the same request. Wire is `.strict()` so a stale-
//   bundle client running against a new backend that adds keys rejects
//   rather than silently dropping them.
export const RequestCarrierInsuranceQuoteSchema = z
  .object({
    id: z.string().uuid(),
    percent: z.number().min(0).max(100),
    comment: z.string(),
    quotedAt: z.string().datetime(),
    needsReconfirm: z.boolean(),
    amount: z.number().nullable(),
    currency: CarrierBidCurrencySchema.nullable(),
    // #1163 — scope discriminator + target id. Reuses `CarrierBidScope`
    //   verbatim (no rename) — the enum name carries `Bid` for
    //   historical reasons but the values are domain-neutral.
    scope: CarrierBidScopeSchema,
    scopeRefId: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine((quote, ctx) => {
    if (quote.scope === 'REQUEST') {
      if (quote.scopeRefId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scopeRefId'],
          message: 'must be null when scope = REQUEST',
        });
      }
    } else if (quote.scopeRefId === null || quote.scopeRefId.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeRefId'],
        message: 'required when scope != REQUEST',
      });
    }
  });
export type RequestCarrierInsuranceQuote = z.infer<typeof RequestCarrierInsuranceQuoteSchema>;

export const RequestCarrierSchema = z
  .object({
    // WHY (#702): server-minted `RequestCarrier.id` UUID. With per-
    //   destination scope the legacy `(requestId, carrierId)` pair is
    //   no longer a unique row identifier — the wire carries the row
    //   id explicitly so PATCH/DELETE/bid routes address one specific
    //   `(carrierId, arrivalCityId)` instance instead of guessing.
    id: z.string().uuid(),
    carrierId: z.string().regex(/^TR-\d{4}$/, 'must match TR-NNNN'),
    carrierName: z.string().min(1),
    // WHY (#702 / #1053): destination scope. STANDARD requests carry the
    //   destination FK (uuid) — the same carrier may be added once per
    //   destination city. CONTAINER requests carry NULL — the carrier
    //   covers the whole request. The aggregate refine in `_base.ts`
    //   enforces the parent-kind ↔ arrivalCityId pairing; the service
    //   validates a non-null id resolves to one of the parent's
    //   `RequestDestination.arrivalCityId` values.
    arrivalCityId: z.string().uuid().nullable(),
    state: z.nativeEnum(RequestCarrierState),
    bids: z.array(RequestCarrierBidSchema),
    rejectReason: z.string().nullable(),
    // WHY (#1052/#1163): per-carrier insurance quotes. Originally a
    //   single nullable slot; #1163 widens it to an array with exactly
    //   zero or one REQUEST-scope primary plus any number of
    //   `ITEM`/`BOX`/`BOX_ITEM`/`BOX_GROUP` overrides. The service
    //   rejects writes when `Request.insuranceRequested=false` and
    //   rejects toggling the flag off while any row exists
    //   (DOMAIN_CONFLICT). Stable read order: primary first, then by
    //   scope (`ITEM`, `BOX`, `BOX_ITEM`, `BOX_GROUP`).
    insuranceQuotes: z.array(RequestCarrierInsuranceQuoteSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RequestCarrier = z.infer<typeof RequestCarrierSchema>;

// WHY (#1052): pure helper for computed insurance amount. Used by the
//   backend read-model projector and (optionally) by web. Returns null
//   when either input is missing — defensive against rows where the
//   parent cargo value has been canonicalised to null (e.g. flag was
//   toggled off and quote was removed).
export function computeInsuranceAmount(
  cargoValue: number | null,
  percent: number | null,
): number | null {
  if (cargoValue === null || percent === null) return null;
  return (cargoValue * percent) / 100;
}

// WHY (#1052): bounds for `percent`. Exported so DTOs (create / update
//   carrier insurance quote) reuse the exact same min/max as the read
//   schema instead of repeating literals.
export const CARRIER_INSURANCE_PERCENT_MIN = 0;
export const CARRIER_INSURANCE_PERCENT_MAX = 100;
export const CARRIER_INSURANCE_PERCENT_DEFAULT = 1;
